import { useEffect, useMemo, useRef, useState } from "react";
import { initMercadoPago, Payment } from "@mercadopago/sdk-react";
import { CheckCircle2, Circle, Copy, Clock, XCircle, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, brl, dt, errMsg } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

let mpInit = null;

export function PaymentBrick({ order, onPaid }) {
  const { settings } = useStore();
  const pk = settings?.payment?.public_key;
  const busy = useRef(false);
  const [ready, setReady] = useState(false);
  const [pixBusy, setPixBusy] = useState(false);
  const maxInst = settings?.max_installments || 12;
  const initialization = useMemo(() => ({ amount: Number(order.total), payer: { email: order.email } }), [order.total, order.email]);
  const customization = useMemo(() => ({ paymentMethods: { creditCard: "all", debitCard: "all", ticket: "all", maxInstallments: maxInst },
    visual: { style: { theme: "dark", customVariables: { baseColor: "#008CFF" } } } }), [maxInst]);
  useEffect(() => {
    if (pk && mpInit !== pk) { initMercadoPago(pk, { locale: "pt-BR" }); mpInit = pk; }
    // Brick must be unmounted on route change
    return () => { try { window.paymentBrickController?.unmount(); } catch (e) { /* noop */ } };
  }, [pk]);
  if (!settings?.payment?.configured) return (
    <div className="card p-6 border-[#008CFF]/30" data-testid="payment-unavailable">
      <h3 className="font-semibold">Pagamento online temporariamente indisponível</h3>
      <p className="text-sm text-[#A0A0A0] mt-2">Seu pedido <b className="text-white">{order.number}</b> foi registrado e está aguardando pagamento. A loja ainda está conectando o Mercado Pago — fale conosco pelo WhatsApp para concluir.</p>
    </div>
  );
  const onSubmit = async ({ selectedPaymentMethod, formData }) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const r = await api.post(`/checkout/pay/${order.id}`, { token: order.token, form_data: formData, selected: selectedPaymentMethod });
      onPaid(r.data);
    } catch (e) { toast.error(errMsg(e)); throw e; } finally { busy.current = false; }
  };
  const payPix = async () => {
    if (busy.current) return;
    busy.current = true;
    setPixBusy(true);
    try {
      const r = await api.post(`/checkout/pay/${order.id}`, { token: order.token, form_data: { payment_method_id: "pix", payer: { email: order.email } }, selected: "bank_transfer" });
      onPaid(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { busy.current = false; setPixBusy(false); }
  };
  return (
    <div data-testid="payment-brick" className="space-y-4">
      <div className="card p-5 border-[#00C8FF]/40 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="pix-direct">
        <div className="flex-1"><div className="font-semibold">Pix · aprovação na hora</div>
          <div className="text-xs text-[#A0A0A0] mt-1">{settings?.pix_discount_percent ? `${settings.pix_discount_percent}% de desconto. ` : ""}O QR Code e o código copia e cola são gerados imediatamente.</div></div>
        <button className="btn-primary py-3.5" onClick={payPix} disabled={pixBusy} data-testid="pix-direct-btn">{pixBusy ? "Gerando..." : "Pagar com Pix"}</button>
      </div>
      <div className="text-xs uppercase tracking-wider text-[#6b7079] text-center">ou pague com cartão ou boleto</div>
      {!ready && <div className="h-64 rounded-2xl bg-[#171A1F] animate-pulse" />}
      <Payment initialization={initialization} customization={customization}
        onReady={() => setReady(true)} onError={(e) => console.error(e)} onSubmit={onSubmit} />
    </div>
  );
}

export function PixBox({ pay }) {
  if (!pay?.qr_code) return null;
  const copy = () => { navigator.clipboard.writeText(pay.qr_code); toast.success("Código Pix copiado"); };
  return (
    <div className="card p-6 text-center" data-testid="pix-box">
      <h3 className="font-semibold text-lg">Pague com Pix</h3>
      {pay.expires_at && <p className="text-xs text-[#A0A0A0] mt-1 flex items-center justify-center gap-1"><Clock className="h-3.5 w-3.5" />Expira em {dt(pay.expires_at)}</p>}
      {pay.qr_code_base64 && <img src={`data:image/png;base64,${pay.qr_code_base64}`} alt="QR Code Pix" className="mx-auto mt-4 h-56 w-56 rounded-xl bg-white p-2" data-testid="pix-qr" />}
      <div className="mt-4 text-left"><label className="label">Pix copia e cola</label>
        <div className="flex gap-2"><input readOnly className="input font-mono text-xs" value={pay.qr_code} data-testid="pix-code" /><button className="btn-primary" onClick={copy} data-testid="pix-copy"><Copy className="h-4 w-4" /></button></div></div>
      <p className="text-xs text-[#A0A0A0] mt-4">Esta página atualiza automaticamente quando o pagamento for confirmado.</p>
    </div>
  );
}

const STEPS = [["recebido", "Pedido realizado"], ["pagamento_aprovado", "Pagamento aprovado"], ["em_producao", "Produção iniciada"], ["producao_concluida", "Produto finalizado"], ["enviado", "Enviado"], ["entregue", "Entregue"]];

export function Timeline({ order }) {
  const at = (s) => order.history?.find((h) => h.status === s)?.at;
  const reached = (k) => {
    if (at(STEPS[k][0])) return true;
    return STEPS.slice(k + 1).some(([s]) => at(s)) || (STEPS[k][0] === "producao_concluida" && at("em_preparacao"));
  };
  return (
    <div data-testid="order-timeline">
      <ol className="space-y-4">
        {STEPS.map(([s, l], k) => {
          const ok = reached(k);
          return (
            <li key={s} className="flex items-start gap-3">
              {ok ? <CheckCircle2 className="h-5 w-5 text-[#00C8FF] shrink-0" /> : <Circle className="h-5 w-5 text-[#3a3f4a] shrink-0" />}
              <div><div className={`text-sm font-semibold ${ok ? "" : "text-[#6b7079]"}`}>{l}</div>{at(s) && <div className="text-xs text-[#A0A0A0]">{dt(at(s))}</div>}</div>
            </li>
          );
        })}
      </ol>
      {["cancelado", "reembolsado"].includes(order.status) && <div className="mt-4 flex items-center gap-2 text-sm text-red-400"><XCircle className="h-4 w-4" />{order.status === "cancelado" ? "Pedido cancelado" : "Pedido reembolsado"}</div>}
      {order.tracking?.code && (
        <div className="mt-5 p-4 rounded-xl bg-[#0B0D11] border border-[#282D37] text-sm" data-testid="tracking-info">
          <div className="flex items-center gap-2 font-semibold"><Truck className="h-4 w-4 text-[#00C8FF]" />{order.tracking.carrier || "Transportadora"}</div>
          <div className="font-mono mt-1">{order.tracking.code}</div>
          {order.tracking.url && <a href={order.tracking.url} target="_blank" rel="noreferrer" className="text-[#00C8FF] underline text-xs">Rastrear envio</a>}
        </div>
      )}
    </div>
  );
}

export const OrderItems = ({ order }) => (
  <div className="space-y-2">
    {order.items.map((i, k) => (
      <div key={k} className="flex justify-between gap-3 text-sm">
        <span>{i.qty}× {i.name} <span className="text-[#A0A0A0] text-xs">{[...Object.values(i.colors || {}), ...Object.values(i.options || {})].filter(Boolean).join(" · ")}</span></span>
        <span className="font-semibold whitespace-nowrap">{brl(i.total)}</span>
      </div>
    ))}
  </div>
);
